import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FaClock, FaUser, FaFolder, FaTags, FaArrowLeft } from 'react-icons/fa';
import { blogPosts } from './blogData';

const PageContainer = styled.div`
  margin-top: 180px;
  min-height: calc(100vh - 140px);
  background: #ffffff;
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) { margin-top: 155px; }


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

const Container = styled.div` max-width: 850px; margin: 0 auto; padding: 2.5rem 5% 3rem; `;

const BackLink = styled(Link)`
  display: inline-flex; align-items: center; gap: 0.5rem;
  color: #3730A3; text-decoration: none; font-weight: 600; margin-bottom: 2rem;
  &:hover { color: #0b0b77; }
`;

const PostHeader = styled.div` text-align: center; margin-bottom: 2.5rem; `;

const Category = styled.span`
  background: #f5f3ff; color: #3730A3; padding: 0.3rem 1rem; border-radius: 30px;
  font-size: 0.85rem; font-weight: 700; display: inline-block; margin-bottom: 1rem;
`;

const Title = styled.h1`
  font-family: 'Playfair Display', 'Times New Roman', serif;
  color: #1a1a2e; font-size: 2.2rem; margin-bottom: 1rem; line-height: 1.3;
  @media (max-width: 768px) { font-size: 1.6rem; }
`;

const MetaInfo = styled.div`
  display: flex; justify-content: center; gap: 1.5rem; color: #4a4a6a; font-size: 0.9rem;
  flex-wrap: wrap;
  span { display: flex; align-items: center; gap: 0.4rem; }
`;

const FeaturedImage = styled.img`
  width: 100%; max-height: 400px; object-fit: cover; border-radius: 16px;
  margin-bottom: 2.5rem; box-shadow: 0 10px 30px rgba(55,48,163,0.08);
`;

const Content = styled.div`
  background: white; padding: 2.5rem; border-radius: 16px;
  box-shadow: 0 4px 15px rgba(55,48,163,0.04); border: 1px solid #f0f0f5;
  margin-bottom: 2rem;

  h2 { color: #1a1a2e; font-size: 1.5rem; margin: 2rem 0 1rem; font-family: 'Times New Roman', serif; }
  h3 { color: #1a1a2e; font-size: 1.2rem; margin: 1.5rem 0 0.8rem; font-family: 'Times New Roman', serif; }
  h4 { color: #3730A3; font-size: 1.05rem; margin: 1.2rem 0 0.6rem; font-family: 'Times New Roman', serif; }
  p { color: #4a4a6a; line-height: 1.8; margin-bottom: 1.2rem; font-size: 1rem; }
  ul, ol { margin: 0.8rem 0 1.2rem 1.5rem; color: #4a4a6a; line-height: 1.8; }
  li { margin-bottom: 0.4rem; }
  strong { color: #3730A3; }
`;

const TagsSection = styled.div`
  display: flex; align-items: center; gap: 0.8rem; flex-wrap: wrap;
  padding: 1.2rem 0; border-top: 1px solid #f0f0f5; border-bottom: 1px solid #f0f0f5;
  margin-bottom: 2rem;
`;

const TagsLabel = styled.span`
  display: flex; align-items: center; gap: 0.4rem; color: #1a1a2e; font-weight: 700;
`;

const Tag = styled(Link)`
  background: #f5f3ff; color: #3730A3; padding: 0.3rem 0.9rem; border-radius: 20px;
  font-size: 0.8rem; text-decoration: none; font-weight: 600;
  &:hover { background: #3730A3; color: white; }
`;

const RelatedSection = styled.div` margin-top: 3rem; `;

const RelatedTitle = styled.h3`
  font-family: 'Times New Roman', serif;
  color: #1a1a2e; font-size: 1.3rem; margin-bottom: 1.5rem;
`;

const RelatedGrid = styled.div`
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.2rem;
  @media (max-width: 500px) { grid-template-columns: 1fr; }
`;

const RelatedCard = styled(Link)`
  text-decoration: none; background: white; border-radius: 10px; overflow: hidden;
  box-shadow: 0 2px 10px rgba(55,48,163,0.04); border: 1px solid #f0f0f5;
  transition: all 0.3s ease;
  &:hover { transform: translateY(-3px); box-shadow: 0 8px 20px rgba(55,48,163,0.08); }
`;

const RelatedImage = styled.img` width: 100%; height: 100px; object-fit: cover; `;

const RelatedContent = styled.div` padding: 0.8rem 1rem; `;

const RelatedPostTitle = styled.h4`
  font-family: 'Times New Roman', serif;
  font-size: 0.9rem; color: #1a1a2e; margin-bottom: 0.2rem; line-height: 1.3;
`;

const RelatedDate = styled.span` color: #888; font-size: 0.75rem; `;

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    const foundPost = blogPosts.find(p => p.slug === slug);
    if (foundPost) {
      setPost(foundPost);
      const related = blogPosts.filter(p => p.category === foundPost.category && p.id !== foundPost.id).slice(0, 2);
      setRelatedPosts(related);
    } else {
      navigate('/blog');
    }
    window.scrollTo(0, 0);
  }, [slug, navigate]);

  if (!post) return null;

  return (
    <PageContainer>
      <Container>
        <BackLink to="/blog"><FaArrowLeft /> Back to Blog</BackLink>

        <PostHeader>
          <Category>{post.category}</Category>
          <Title>{post.title}</Title>
          <MetaInfo>
            <span><FaUser /> {post.author}</span>
            <span><FaClock /> {post.date} · {post.readTime}</span>
            <span><FaFolder /> {post.category}</span>
          </MetaInfo>
        </PostHeader>

        <FeaturedImage src={post.image} alt={post.title} />

        <Content dangerouslySetInnerHTML={{ __html: post.content }} />

        <TagsSection>
          <TagsLabel><FaTags /> Tags:</TagsLabel>
          {post.tags.map((tag, i) => <Tag key={i} to={`/blog`}>{tag}</Tag>)}
        </TagsSection>

        {relatedPosts.length > 0 && (
          <RelatedSection>
            <RelatedTitle>Related Articles</RelatedTitle>
            <RelatedGrid>
              {relatedPosts.map(r => (
                <RelatedCard key={r.id} to={`/blog/${r.slug}`}>
                  <RelatedImage src={r.image} alt={r.title} />
                  <RelatedContent>
                    <RelatedPostTitle>{r.title}</RelatedPostTitle>
                    <RelatedDate>{r.date}</RelatedDate>
                  </RelatedContent>
                </RelatedCard>
              ))}
            </RelatedGrid>
          </RelatedSection>
        )}
      </Container>
    </PageContainer>
  );
};

export default BlogPost;